import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Chart, ChartOptions, registerables } from 'chart.js';
import {
  DataService,
  PamlabApiServiceService,
  SnackbarService,
} from '@/shared/services';
import {
  StudentTypesFund,
  StudentFinancialAidDataReturn,
} from '../../../../server/types';
export interface ChartDataset {
  label: string;
  data: number[];
}

export interface FundSoucesDataset {
  [source: string]: ChartDataset | undefined;
  state?: ChartDataset;
  transfer?: ChartDataset;
  continuing?: ChartDataset;
}

export interface FundTypesDataset {
  [type: string]: ChartDataset | undefined;
  ACEF?: ChartDataset;
  ACEX?: ChartDataset;
  NAUF?: ChartDataset;
  NATR?: ChartDataset;
  NAUT?: ChartDataset;
  ARG?: ChartDataset;
  PELL?: ChartDataset;
  SEOF?: ChartDataset;
  FGMF?: ChartDataset;
  FSGF?: ChartDataset;
  FAGF?: ChartDataset;
  EHNF?: ChartDataset;
  GAP?: ChartDataset;
  SEOT?: ChartDataset;
  FSGT?: ChartDataset;
  FAGT?: ChartDataset;
  EHNT?: ChartDataset;
  TSGR?: ChartDataset;
  FSAG?: ChartDataset;
  SEOG?: ChartDataset;
  FGMG?: ChartDataset;
  FAGR?: ChartDataset;
  EHAN?: ChartDataset;
  FAOS?: ChartDataset;
  ISGF?: ChartDataset;
}

@Component({
  selector: 'financial-aid-tool-content',
  templateUrl: './financial-aid-tool-content.component.html',
  styleUrls: ['./financial-aid-tool-content.component.css'],
  standalone: false,
})
export class FinancialAidToolContentComponent implements OnInit, OnDestroy {
  public dataPending!: boolean;
  private subscription: Subscription = new Subscription();
  public receivedData: boolean = false;
  public chartLabels!: string[];
  public studentTypeFundDataset!: ChartDataset[];
  public fundSourcesDataset!: ChartDataset[];
  public fundTypesDataset!: ChartDataset[];
  public chartConfig: ChartOptions = {
    // responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          font: {
            size: 15,
            family: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Year',
          font: {
            size: 16,
            family: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
          },
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Amount($)',
          font: {
            size: 16,
            family: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
          },
        },
      },
    },
  };

  public studentTypeFundAmountChart!: Chart;
  public fundSourcesAmountChart!: Chart;
  public fundTypesAmountChart!: Chart;

  constructor(
    private dataService: DataService,
    private snackbarService: SnackbarService,
    private apiService: PamlabApiServiceService,
  ) {
    Chart.register(...registerables);
  }

  ngOnInit(): void {
    this.subscription.add(
      this.dataService.getData().subscribe((dataStream) => {
        if (dataStream.data) {
          const data = dataStream.data as StudentFinancialAidDataReturn;
          this.receivedData = true;
          this.setUpFinancialAidChartData(data.chart_data);
          this.snackbarService.open('Data received');
        }

        dataStream.error &&
          this.snackbarService.open(dataStream.error.message, 'error');
      }),
    );

    this.subscription.add(
      this.dataService.getPendingData().subscribe((pending) => {
        this.dataPending = pending;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  setUpFinancialAidChartData(data: StudentTypesFund) {
    this.studentTypeFundDataset = [];
    const yearLabels: string[] = [];
    const tempSourcesDataset: FundSoucesDataset = {};
    const tempTypesDataset: FundTypesDataset = {};

    Object.keys(data).forEach((stuType) => {
      const yearsFundData = data[stuType]!;
      const tempDataSet: { label: string; data: any[] } = {
        label: stuType,
        data: [],
      };
      Object.keys(yearsFundData).forEach((year) => {
        tempDataSet.data.push(yearsFundData[year]?.total);
        yearLabels.push(year);

        const fundSourcesData = yearsFundData[year]!.fund_sources;
        Object.keys(fundSourcesData).forEach((source) => {
          if (!tempSourcesDataset[source + stuType]) {
            tempSourcesDataset[source + stuType] = {
              label:
                stuType + ' - ' + source[0].toUpperCase() + source.substring(1),
              data: [],
            };
          }
          tempSourcesDataset[source + stuType]?.data.push(
            fundSourcesData[source]!.total,
          );

          const fundTypesData = fundSourcesData[source]!.fund_types;
          Object.keys(fundTypesData).forEach((type) => {
            if (!tempTypesDataset[stuType + type]) {
              tempTypesDataset[stuType + type] = {
                label: stuType + ' - ' + type,
                data: [],
              };
            }
            tempTypesDataset[stuType + type]?.data.push(fundTypesData[type]!);
          });
        });
      });
      this.studentTypeFundDataset.push(tempDataSet);
    });

    this.chartLabels = [...new Set(yearLabels)];
    this.fundSourcesDataset = <ChartDataset[]>Object.values(tempSourcesDataset);
    this.fundTypesDataset = <ChartDataset[]>Object.values(tempTypesDataset);

    this.createStudentTypesFundAmountChart();
  }

  createStudentTypesFundAmountChart() {
    this.studentTypeFundAmountChart &&
      this.studentTypeFundAmountChart.destroy();

    setTimeout(() => {
      this.studentTypeFundAmountChart = new Chart(
        'student-type-fund-amount-chart',
        {
          type: 'line',
          data: {
            labels: this.chartLabels,
            datasets: this.studentTypeFundDataset,
          },
          options: this.chartConfig,
        },
      );
      this.studentTypeFundAmountChart.update();

      this.fundSourcesAmountChart && this.fundSourcesAmountChart.destroy();

      this.fundSourcesAmountChart = new Chart('fund-sources-amount-chart', {
        type: 'line',
        data: {
          labels: this.chartLabels,
          datasets: this.fundSourcesDataset,
        },
        options: this.chartConfig,
      });
      this.fundSourcesAmountChart.update();

      this.fundTypesAmountChart && this.fundTypesAmountChart.destroy();

      this.fundTypesAmountChart = new Chart('fund-types-amount-chart', {
        type: 'line',
        data: {
          labels: this.chartLabels,
          datasets: this.fundTypesDataset,
        },
        options: this.chartConfig,
      });
      this.fundTypesAmountChart.update();
    });
  }
}
